import { atom, getDefaultStore, type Atom } from "jotai";
import { produce } from "immer";

/**
 * Colour themes are a Bootstrap 5.3 style colour mode: a theme id is the value written to
 * `data-bs-theme` on <html>, and the palette behind it is a block in
 * `src/assets/scss/_themes.scss`.
 * See https://getbootstrap.com/docs/5.3/customize/color-modes/
 *
 * `auto` is the one id with no stylesheet behind it - it resolves to `light` or `dark`
 * from the operating system setting.
 */
export const AUTO_THEME = "auto";
export const LIGHT_THEME = "light";
export const DARK_THEME = "dark";

export interface AppTheme {
    /** The `data-bs-theme` value, and what we persist for the user. */
    id: string;
    /** Localization key for the switcher label. */
    labelKey: string;
    /** Shown when the language texts carry no string for `labelKey` yet. */
    fallbackLabel: string;
}

export interface ThemeState {
    /** What the user picked - one of APP_THEMES, possibly `auto`. */
    preference: string;
    /** What is actually on <html> - `auto` resolved against the OS setting. */
    resolved: string;
    /** True when the resolved theme is a dark one. */
    isDark: boolean;
}

/**
 * The themes offered in the switcher. Adding one is two steps: a colour-mode block in
 * `_themes.scss` and an entry here (plus its localized string). Nothing else enumerates
 * themes - the header switcher renders straight off this list.
 */
export const APP_THEMES: AppTheme[] = [
    { id: AUTO_THEME, labelKey: "theme.auto", fallbackLabel: "System" },
    { id: LIGHT_THEME, labelKey: "theme.light", fallbackLabel: "Light" },
    { id: DARK_THEME, labelKey: "theme.dark", fallbackLabel: "Dark" },
];

export const DEFAULT_THEME = AUTO_THEME;

/**
 * How the service reaches the logged-in user and the server. Handed in by `main.tsx`
 * rather than imported: the user and API services sit in an import cycle with half the
 * app, and components import this module, so it has to stay a leaf. The implementation
 * is `themeUserBinding` in `src/services/user/userService`.
 */
export interface ThemeUserBinding {
    /** The logged-in user, carrying the preference stored on the server. */
    userAtom: Atom<{ Id?: string; ColorTheme?: string } | null>;
    /** Persists the preference against the logged-in user. May reject. */
    saveTheme(preference: string): Promise<unknown>;
    /** Updates the cached copy of the user that is restored on the next reload. */
    rememberTheme(preference: string): void;
}

/**
 * Mirrors the server-side preference so the very first paint after a reload already has
 * the right palette - the inline bootstrapping script in `index.html` reads this key
 * before the bundle loads. The server remains the source of truth; this is overwritten
 * from the logged-in user. Keep the key and the `auto` resolution in sync with that script.
 *
 * The server side is the one portal4 uses (`PUT /user/settings/colortheme`, read back as
 * `CurrentUser.ColorTheme`), so the preference follows the user between the two apps.
 */
export const THEME_STORAGE_KEY = "ln-color-theme";

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

export function isKnownTheme(id: string | null | undefined): id is string {
    return !!id && APP_THEMES.some(t => t.id === id);
}

/** True when the operating system is currently asking for a dark palette. */
export function prefersDark(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia(DARK_MEDIA_QUERY).matches;
}

/**
 * The theme id actually applied to the document - i.e. `auto` mapped onto the OS setting.
 * Pure: the caller passes the OS state in, so it can be tested without a media query.
 */
export function resolveTheme(preference: string, systemPrefersDark: boolean): string {
    if (preference !== AUTO_THEME) return preference;
    return systemPrefersDark ? DARK_THEME : LIGHT_THEME;
}

export function readStoredTheme(): string {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        return isKnownTheme(stored) ? stored : DEFAULT_THEME;
    } catch {
        // Storage can be unavailable (private mode, blocked cookies). The app still runs,
        // it just starts from the default every time.
        return DEFAULT_THEME;
    }
}

export const themeStateAtom = atom<ThemeState>({
    preference: DEFAULT_THEME,
    resolved: LIGHT_THEME,
    isDark: false,
});
themeStateAtom.debugLabel = "themeStateAtom";

export class ThemeService {
    private store = getDefaultStore();
    private unsubscribers: (() => void)[] = [];
    private user: ThemeUserBinding | null = null;
    private saveQueue: Promise<void> = Promise.resolve();

    /**
     * Wires the service up to the browser and, when given, to the logged-in user. Called
     * once from `main.tsx`; safe to call again (it tears down the previous subscriptions first).
     */
    init(user?: ThemeUserBinding): void {
        this.dispose();
        this.user = user ?? null;

        this.applyPreference(readStoredTheme(), { persist: false });

        // While the preference is `auto`, the OS flipping light/dark has to move with it.
        if (typeof window.matchMedia === "function") {
            const query = window.matchMedia(DARK_MEDIA_QUERY);
            const onChange = () => this.applyPreference(this.getPreference(), { persist: false });
            query.addEventListener("change", onChange);
            this.unsubscribers.push(() => query.removeEventListener("change", onChange));
        }

        // The server is the source of truth: adopt the logged-in user's preference
        // whenever it arrives or changes (login, relogin, user switch).
        // The user can already be in the atom (restored from the cache before init runs),
        // and store.sub only fires on later changes, so adopt once up front as well.
        if (user) {
            const adoptUserTheme = () => {
                const serverTheme = this.store.get(user.userAtom)?.ColorTheme;
                if (isKnownTheme(serverTheme) && serverTheme !== this.getPreference()) {
                    this.applyPreference(serverTheme, { persist: false });
                }
            };
            adoptUserTheme();
            this.unsubscribers.push(this.store.sub(user.userAtom, adoptUserTheme));
        }
    }

    /** Removes the media-query and login subscriptions. Used by init() and by tests. */
    dispose(): void {
        this.unsubscribers.forEach(unsubscribe => unsubscribe());
        this.unsubscribers = [];
        this.user = null;
    }

    getPreference(): string {
        return this.store.get(themeStateAtom).preference;
    }

    getState(): ThemeState {
        return this.store.get(themeStateAtom);
    }

    /** What the user picked in the switcher. Persists against the logged-in user. */
    setTheme(preference: string): void {
        if (!isKnownTheme(preference) || preference === this.getPreference()) return;
        this.applyPreference(preference, { persist: true });
    }

    /**
     * The single place that moves the app onto a theme: updates the atom, the
     * `data-bs-theme` attribute every colour-mode rule hangs off, the no-flash
     * mirror, and - when the change came from the user - the server.
     */
    private applyPreference(preference: string, { persist }: { persist: boolean }): void {
        const resolved = resolveTheme(preference, prefersDark());

        this.store.set(
            themeStateAtom,
            produce(this.store.get(themeStateAtom), draft => {
                draft.preference = preference;
                draft.resolved = resolved;
                draft.isDark = resolved === DARK_THEME;
            }),
        );

        this.setDocumentTheme(resolved);

        this.storeTheme(preference);

        if (persist) {
            void this.save(preference);
        }
    }

    /** Writes the resolved theme onto <html>, which is what the stylesheets key off. */
    setDocumentTheme(resolved: string): void {
        document.documentElement.setAttribute("data-bs-theme", resolved);
    }

    private storeTheme(preference: string): void {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, preference);
        } catch {
            // See readStoredTheme.
        }
    }

    /**
     * Persists the preference against the logged-in user so it follows them between
     * browsers. Errors are swallowed: a theme that only lives in localStorage is a far
     * better outcome than an error dialog over a colour change.
     *
     * The user's cached copy is updated as well: it is put back on reload before the
     * login reply arrives, and its stale `ColorTheme` would otherwise be adopted and flip
     * the app back for a moment.
     */
    private save(preference: string): void {
        const user = this.user;
        const owner = user && this.store.get(user.userAtom);
        if (!user || !owner) return;

        try {
            user.rememberTheme(preference);
        } catch {
            // Ignored on purpose - see above.
        }

        // One request at a time, in the order the user picked: two PUTs in flight can
        // land out of order and leave the server on the older choice. A save still queued
        // when its user logs out or switches account is dropped, since the shared API
        // client would send it with the next account's credentials.
        this.saveQueue = this.saveQueue
            .then(() => {
                const current = this.store.get(user.userAtom);
                if (!current || current.Id !== owner.Id) return;
                return user.saveTheme(preference);
            })
            .then(() => undefined, () => undefined);
    }

}

export const themeService = new ThemeService();
